/** @license
 *
 * Copyright 2022 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

import * as intl from "@arcgis/core/intl";

/**
 * Determines the direction to use in the app and sets it in the html tag.
 *
 * @param locale Locale; defaults to "en"
 *
 * @return Returns true if direction is "rtl" for locale
 */
export function decideAndSetDir(
  locale: string = "en"
): boolean {
  const dir = getLanguageDirection(locale);
  document.querySelector("html")?.setAttribute("dir", dir);
  return dir === "rtl";
}

/**
 * Determines the locale to use for starting the app and sets it in the html tag.
 *
 * @param urlSearch Search portion of URL, e.g., from window.location.search
 * @param portal Portal info if available
 * @param browserLang Preferred language of the user, e.g., from window.navigator.language
 *
 * @return The determined locale string
 */
export function decideAndSetLocale(
  urlSearch?: string,
  portal?: __esri.Portal,
  browserLang?: string
): string {
  // Query String normalized to "xx-yy" format (case insensitive)
  const urlParams = new URLSearchParams(urlSearch);
  let queryStrLocale: string | null | undefined = urlParams.get('locale');
  if (queryStrLocale) {
    queryStrLocale = filterToSupportedLocales(queryStrLocale.replace(/_/g, "-"));
  }
  // User Profile
  const userLocale: string | null | undefined = filterToSupportedLocales(portal?.get("user.culture"));
  // Browser
  const browserLocale: string | null | undefined = filterToSupportedLocales(browserLang);
  // Current Esri set locale
  let locale: string = intl.getLocale();

  /* Order of precedence for locale:
   *   1st- query string
   *   2nd- locale set in user profile,
   *   3rd- browser (window.navigator.language)
   *   4th- locale currently set in JSAPI, which defaults to "en"
  **/
  locale = (queryStrLocale ?? userLocale ?? browserLocale ?? locale).toLowerCase();
  document.querySelector("html")?.setAttribute("lang", locale);
  intl.setLocale(locale);
  return locale;
}

/**
 * Filters the locale to supported locales.
 *
 * @param locale Locale to filter
 *
 * @return Returns the locale if supported, otherwise null
 */
export function filterToSupportedLocales(
  locale: string | null | undefined
): string | null | undefined {
  // Do we support this locale?
  const normalizedLocale = intl.normalizeMessageBundleLocale((locale ?? "") as any);
  return normalizedLocale ? locale : null;
}

/**
 * Determines the direction to use in the app.
 *
 * @param locale Normalized locale; defaults to "en"
 *
 * @return Returns "rtl" or "ltr" appropriate for locale
 */
export function getLanguageDirection(
  locale: string = "en"
): string {
  const rtlLocales: string[] = ["ar", "he"];

  const isRTL = rtlLocales.some(language => {
    return locale.indexOf(language) === 0;
  });
  return isRTL ? "rtl" : "ltr";
}
